import { ErrorHandler, Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class GlobalErrorHandlerService implements ErrorHandler {
  handleError(error: any): void {
    // 1. Log the error immediately so it's visible in console before potential reload
    console.error('Global Error Handler caught an error:', error);

    // 2. Extract detailed error messages recursively to detect chunk/asset loading failures
    const errorDetails = this.extractErrorDetails(error);

    const isChunkError = 
      /Loading chunk [\d]+ failed/i.test(errorDetails) || 
      /ChunkLoadError/i.test(errorDetails) ||
      errorDetails.includes('Failed to fetch dynamically imported module') ||
      errorDetails.includes('MIME type');

    if (isChunkError) {
      const hasReloadedRecently = this.checkAndSetReloadFlag();
      if (!hasReloadedRecently) {
        console.warn('Chunk or dynamic import failure detected. Forcing reload to fetch updated assets...');
        window.location.reload();
      } else {
        console.error('Chunk or dynamic import failure persists after reload. Stopping auto-reload to prevent infinite loop.');
      }
    }
  }

  private extractErrorDetails(error: any): string {
    if (!error) {
      return '';
    }

    const parts: string[] = [];
    const visited = new Set<any>();

    const traverse = (obj: any, depth = 0) => {
      if (!obj || depth > 4 || visited.has(obj)) {
        return;
      }
      if (typeof obj === 'string') {
        parts.push(obj);
        return;
      }
      visited.add(obj);

      // Check common error properties
      if (typeof obj.name === 'string') parts.push(obj.name);
      if (typeof obj.message === 'string') parts.push(obj.message);
      if (typeof obj.stack === 'string') parts.push(obj.stack);

      // Traverse nested error properties commonly found in Angular/ZoneJS
      if (obj.originalError) traverse(obj.originalError, depth + 1);
      if (obj.rejection) traverse(obj.rejection, depth + 1);
      if (obj.error) traverse(obj.error, depth + 1);

      // Handle custom toString implementations
      if (typeof obj.toString === 'function') {
        try {
          const str = obj.toString();
          if (str && str !== '[object Object]') {
            parts.push(str);
          }
        } catch (e) {
          // ignore toString errors
        }
      }
    };

    traverse(error);
    return parts.join(' ').trim();
  }

  private checkAndSetReloadFlag(): boolean {
    const timestampKey = 'chunk-reload-timestamp';
    const now = Date.now();
    const minInterval = 15000; // 15 seconds limit between reloads

    try {
      const lastReloadStr = sessionStorage.getItem(timestampKey);
      if (lastReloadStr) {
        const lastReload = parseInt(lastReloadStr, 10);
        if (!isNaN(lastReload) && (now - lastReload) < minInterval) {
          return true; // Reloaded recently, do not reload again
        }
      }
      sessionStorage.setItem(timestampKey, now.toString());
      return false;
    } catch (e) {
      // Fallback to window.name if sessionStorage is blocked
      try {
        const match = window.name.match(new RegExp(`${timestampKey}_(\\d+)`));
        if (match) {
          const lastReload = parseInt(match[1], 10);
          if (!isNaN(lastReload) && (now - lastReload) < minInterval) {
            return true;
          }
        }
        const cleanName = window.name ? window.name.replace(new RegExp(`;?${timestampKey}_\\d+`), '') : '';
        window.name = cleanName ? `${cleanName};${timestampKey}_${now}` : `${timestampKey}_${now}`;
        return false;
      } catch (e2) {
        // Safe default: do not reload if we cannot verify reload history
        return true;
      }
    }
  }
}
